import * as React from 'react'
import {
    Alert, Box, Checkbox, Chip, FormControlLabel, MenuItem, Paper, Stack, Switch, TextField, Typography
} from '@mui/material'
import PublicRoundedIcon from '@mui/icons-material/PublicRounded'
import ShieldRoundedIcon from '@mui/icons-material/ShieldRounded'
import UnitIconFrame from './UnitIconFrame'
import { buildSavedAttackTemplatesUrl } from './attackTemplatePlatform'

const PLUGIN_KEY = 'attackaliens'
export const FOREIGN_GLORY_TOOLS = Object.freeze([
    { wodID: 152, label: 'Royal banner', fameBonus: 6, iconUrl: '/assets/itemassets/Units/Elitetool/Elitetool_Unit_royalBanner/Elitetool_Unit_royalBanner--1573584429307.png' },
    { wodID: 153, label: "King's banner", fameBonus: 8, iconUrl: '/assets/itemassets/Units/Elitetool/Elitetool_Unit_kingsBanner/Elitetool_Unit_kingsBanner--1573584429307.png' },
    { wodID: 239, label: "Viscount's banner", fameBonus: 9, iconUrl: '/assets/itemassets/Units/Elitetool/Elitetool_Unit_viscountBanner/Elitetool_Unit_viscountBanner--1603294562852.png' },
    { wodID: 240, label: "Emperor's banner", fameBonus: 10, iconUrl: '/assets/itemassets/Units/Elitetool/Elitetool_Unit_emperorsBanner/Elitetool_Unit_emperorsBanner--1603294562852.png' },
    { wodID: 474, label: "High Queen's banner", fameBonus: 11, iconUrl: '/assets/itemassets/Units/Elitetool/Elitetool_Unit_highQueensBanner/Elitetool_Unit_highQueensBanner--1699527541701.png' },
    { wodID: 476, label: "Supreme monarch's banner", fameBonus: 13, iconUrl: '/assets/itemassets/Units/Elitetool/Elitetool_Unit_supremeMonarchsBanner/Elitetool_Unit_supremeMonarchsBanner--1699527541701.png' },
    { wodID: 734, label: 'Horsetail standard', fameBonus: 5, iconUrl: '/assets/itemassets/Units/Elitetool/Elitetool_Unit_Horsetailbanner/Elitetool_Unit_Horsetailbanner--1573584429307.png' },
    { wodID: 774, label: "Invader's banner", fameBonus: 4, iconUrl: '/assets/itemassets/Units/Eventtool/Eventtool_Unit_AlienInvasionAntiBanner/Eventtool_Unit_AlienInvasionAntiBanner--1573584429307.png' }
])
const DEFAULT_GLORY_TOOL_IDS = Object.freeze(FOREIGN_GLORY_TOOLS.map(tool => tool.wodID))
const DEFAULT_CONFIG = Object.freeze({
    useCoin: false,
    useLeather: false,
    useWarWagon: false,
    espionageEnabled: true,
    enabledGloryToolWodIDs: DEFAULT_GLORY_TOOL_IDS,
    eventDifficulty: 'auto',
    autoGloryLimit: '0',
    savedAttackTemplateId: '',
    savedAttackTemplateName: '',
    savedAttackTemplateOwner: '',
    attackTemplateBuilder: undefined,
    scanIntervalMinutes: '10'
})

const DIFFICULTIES = Object.freeze([
    ['auto', 'Auto — difficulty reported by the event'],
    ['classic', 'Classic / no autoscaling'],
    ['easy', 'Easy'],
    ['easyPlus', 'Easy+'],
    ['intermediate', 'Intermediate'],
    ['intermediatePlus', 'Intermediate+'],
    ['hard', 'Hard — must already be rented'],
    ['hardPlus', 'Hard+ — must already be rented'],
    ['expert', 'Expert — must already be rented'],
    ['expertPlus', 'Expert+ — must already be rented'],
    ['master', 'Master — must already be rented'],
    ['masterPlus', 'Master+ — must already be rented'],
    ['archmaster', 'Archmaster — must already be rented']
])
const DIFFICULTY_KEYS = new Set(DIFFICULTIES.map(([key]) => key))

const normalizePositiveText = (value, fallback) => {
    const parsed = Number(value)
    return String(Number.isFinite(parsed) && parsed > 0 ? parsed : fallback)
}
const cloneValue = value => value === undefined ? undefined :
    (typeof structuredClone === 'function' ? structuredClone(value) : JSON.parse(JSON.stringify(value)))
const normalizeNonNegativeText = (value, fallback = '0') => {
    const parsed = Number(value)
    return String(Number.isFinite(parsed) && parsed >= 0 ? Math.floor(parsed) : fallback)
}
const normalizeConfig = value => {
    const source = value && typeof value === 'object' && !Array.isArray(value) ? value : {}
    return {
        useCoin: Boolean(source.useCoin),
        useLeather: Boolean(source.useLeather),
        useWarWagon: Boolean(source.useWarWagon),
        espionageEnabled: source.espionageEnabled !== false,
        enabledGloryToolWodIDs: Array.isArray(source.enabledGloryToolWodIDs)
            ? [...new Set(source.enabledGloryToolWodIDs.map(Number).filter(id => DEFAULT_GLORY_TOOL_IDS.includes(id)))]
            : [...DEFAULT_GLORY_TOOL_IDS],
        eventDifficulty: DIFFICULTY_KEYS.has(source.eventDifficulty) ? source.eventDifficulty : DEFAULT_CONFIG.eventDifficulty,
        autoGloryLimit: normalizeNonNegativeText(source.autoGloryLimit, DEFAULT_CONFIG.autoGloryLimit),
        savedAttackTemplateId: [undefined, null, ''].includes(source.savedAttackTemplateId) ? '' : String(source.savedAttackTemplateId),
        savedAttackTemplateName: String(source.savedAttackTemplateName ?? ''),
        savedAttackTemplateOwner: String(source.savedAttackTemplateOwner ?? ''),
        attackTemplateBuilder: cloneValue(source.attackTemplateBuilder),
        scanIntervalMinutes: normalizePositiveText(source.scanIntervalMinutes, DEFAULT_CONFIG.scanIntervalMinutes)
    }
}

export const buildForeignCastlesPluginState = (userPlugins, nextConfig, enabled = true) => {
    const plugins = typeof structuredClone === 'function' ? structuredClone(userPlugins ?? {}) : JSON.parse(JSON.stringify(userPlugins ?? {}))
    const current = plugins[PLUGIN_KEY] && typeof plugins[PLUGIN_KEY] === 'object' ? plugins[PLUGIN_KEY] : {}
    Object.assign(current, normalizeConfig(nextConfig), { state: Boolean(enabled) })
    delete current.commanderWhiteList
    plugins[PLUGIN_KEY] = current
    return plugins
}

const GROUP_SX = {
    p: { xs: 1.5, md: 1.8 }, borderRadius: '8px', border: '1px solid rgba(129,146,178,.16)', backgroundColor: 'rgba(16,27,46,.42)'
}
const FIELD_SX = {
    '& .MuiOutlinedInput-root': { minHeight: 40, borderRadius: '8px', backgroundColor: 'rgba(12,23,40,.88)' },
    '& .MuiInputBase-input, & .MuiSelect-select': { color: '#f5f7ff', fontWeight: 700 }
}
function Helper({ children }) {
    return <Typography sx={{ mt: .45, color: '#9fb0cc', fontSize: '.82rem', lineHeight: 1.4 }}>{children}</Typography>
}
function Field({ label, helper, children }) {
    return <Box><Typography sx={{ mb: .65, color: '#f5f7ff', fontWeight: 820, fontSize: '.9rem' }}>{label}</Typography>{children}<Helper>{helper}</Helper></Box>
}
function GoldCheckbox({ checked, onChange, label }) {
    return <FormControlLabel control={<Checkbox checked={checked} onChange={event => onChange(event.target.checked)} sx={{ color: 'rgba(143,180,255,.5)', '&.Mui-checked': { color: '#8fb4ff' } }} />} label={label} sx={{ m: 0, color: '#f5f7ff', '& .MuiFormControlLabel-label': { fontWeight: 700 } }} />
}

export default function ForeignCastlesBotSettingsPanel({ userPlugins, onChange, runtimeStatus, templatePlatform = 'gge' }) {
    const plugin = React.useMemo(
        () => userPlugins?.[PLUGIN_KEY] && typeof userPlugins[PLUGIN_KEY] === 'object' ? userPlugins[PLUGIN_KEY] : {},
        [userPlugins]
    )
    const [draft, setDraft] = React.useState(() => normalizeConfig(plugin))
    const [templates, setTemplates] = React.useState([])
    const enabled = plugin.state === true
    React.useEffect(() => setDraft(normalizeConfig(plugin)), [plugin])
    React.useEffect(() => {
        fetch(buildSavedAttackTemplatesUrl(templatePlatform), { credentials: 'same-origin' })
            .then(response => response.ok ? response.json() : { templates: [] })
            .then(payload => setTemplates([...(Array.isArray(payload?.templates) ? payload.templates : [])]
                .sort((left, right) => Number(right?.updatedAt ?? 0) - Number(left?.updatedAt ?? 0) || String(left?.name ?? '').localeCompare(String(right?.name ?? '')))))
            .catch(() => setTemplates([]))
    }, [templatePlatform])
    const update = updater => {
        const next = { ...draft }
        updater(next)
        setDraft(next)
        onChange(buildForeignCastlesPluginState(userPlugins, next, enabled))
    }
    const setEnabled = state => onChange(buildForeignCastlesPluginState(userPlugins, draft, state))
    const transport = draft.useLeather ? 'leather' : draft.useCoin ? 'coin' : 'walk'
    const setTransport = value => update(next => {
        next.useLeather = value === 'leather'
        next.useCoin = value === 'coin'
    })
    const selectedGloryTools = React.useMemo(() => new Set(draft.enabledGloryToolWodIDs), [draft.enabledGloryToolWodIDs])
    const gloryStock = React.useMemo(() => new Map(
        (Array.isArray(runtimeStatus?.gloryToolStock) ? runtimeStatus.gloryToolStock : [])
            .map(entry => [Number(entry?.wodID), Math.max(0, Number(entry?.amount) || 0)])
    ), [runtimeStatus?.gloryToolStock])
    const toggleGloryTool = (wodID, checked) => update(next => {
        const selected = new Set(next.enabledGloryToolWodIDs)
        if (checked) selected.add(wodID)
        else selected.delete(wodID)
        next.enabledGloryToolWodIDs = DEFAULT_GLORY_TOOL_IDS.filter(id => selected.has(id))
    })
    const selectAttackTemplate = templateID => update(next => {
        const template = templates.find(entry => String(entry?.id ?? '') === String(templateID ?? ''))
        if (!template) {
            next.savedAttackTemplateId = ''
            next.savedAttackTemplateName = ''
            next.savedAttackTemplateOwner = ''
            next.attackTemplateBuilder = undefined
            return
        }
        next.savedAttackTemplateId = String(template.id)
        next.savedAttackTemplateName = String(template.name ?? '')
        next.savedAttackTemplateOwner = String(template.ownerUsername ?? template.owner ?? '')
        next.attackTemplateBuilder = cloneValue(template.template)
    })

    return <Paper sx={{ p: { xs: 2.2, md: 3 }, borderRadius: '8px', border: '1px solid rgba(129,146,178,.18)', background: 'linear-gradient(180deg,rgba(27,40,63,.9),rgba(24,35,56,.86))' }}>
        <Stack spacing={2.4}>
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={1.2}>
                <Stack direction="row" spacing={1.1} alignItems="center">
                    <Box sx={{ width: 34, height: 34, borderRadius: '8px', display: 'grid', placeItems: 'center', backgroundColor: 'rgba(143,180,255,.14)', border: '1px solid rgba(143,180,255,.35)' }}><PublicRoundedIcon sx={{ color: '#8fb4ff' }} /></Box>
                    <Box><Typography sx={{ color: '#f5f7ff', fontSize: '1.4rem', fontWeight: 850 }}>Foreign Castles Bot Settings</Typography>
                        <Typography sx={{ color: '#9fb0cc', fontSize: '.9rem' }}>Foreign Invaders (71) and Bloodcrows (103)</Typography></Box>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center"><Chip size="small" label={runtimeStatus?.status ?? (enabled ? 'Starting' : 'Stopped')} color={enabled ? 'primary' : 'default'} /><Switch checked={enabled} onChange={event => setEnabled(event.target.checked)} /></Stack>
            </Stack>

            <Alert severity="info">The bot never rents a paid difficulty automatically. Hard through Archmaster must already be active in the game; difficulty selection is sent only with zero-ruby mode.</Alert>
            {runtimeStatus?.lastError ? <Alert severity="warning">{runtimeStatus.lastError}</Alert> : null}

            <Box sx={{ ...GROUP_SX, display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2,minmax(0,1fr))' }, gap: 1.6 }}>
                <Field
                    label={draft.eventDifficulty === 'auto' ? 'Glory limit (Auto)' : 'Automatic glory limit'}
                    helper={draft.eventDifficulty === 'auto'
                        ? 'Editable limit for Auto. When the live event score reaches this value, the bot stops attacking. Set 0 for no limit.'
                        : 'For a fixed difficulty this is read automatically from the final individual reward of the live event.'}>
                    <TextField
                        fullWidth
                        type={draft.eventDifficulty === 'auto' ? 'number' : 'text'}
                        disabled={draft.eventDifficulty !== 'auto'}
                        value={draft.eventDifficulty === 'auto' ? draft.autoGloryLimit : (runtimeStatus?.gloryLimit ?? 'Waiting for event')}
                        onChange={event => update(next => { next.autoGloryLimit = event.target.value })}
                        inputProps={{ min: 0 }}
                        sx={FIELD_SX}
                    />
                </Field>
                <Field label="Event difficulty" helper="Auto follows the live event. A manual value filters the matching event camps.">
                    <TextField select fullWidth value={draft.eventDifficulty} onChange={event => update(next => { next.eventDifficulty = event.target.value })} sx={FIELD_SX}>
                        {DIFFICULTIES.map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
                    </TextField>
                </Field>
                <Field label="Travel mode" helper="Leather uses the instant travel option only when it is unlocked; otherwise the bot falls back to walking.">
                    <TextField select fullWidth value={transport} onChange={event => setTransport(event.target.value)} sx={FIELD_SX}>
                        <MenuItem value="walk">Walk / no horse</MenuItem><MenuItem value="coin">Coin horse</MenuItem><MenuItem value="leather">Leather / PTT</MenuItem>
                    </TextField>
                </Field>
                <Field label="Scan interval" helper="Minutes between full world-map scans. The bot can rescan earlier when a commander assigned in Commander Allocation returns.">
                    <TextField fullWidth type="number" value={draft.scanIntervalMinutes} onChange={event => update(next => { next.scanIntervalMinutes = event.target.value })} inputProps={{ min: 1 }} sx={FIELD_SX} />
                </Field>
                <Field label="Attack Template" helper="Imports a saved template from Attack Templates. Selected slots override FILL WAVES; unspecified slots stay automatically filled.">
                    <TextField select fullWidth value={draft.savedAttackTemplateId} onChange={event => selectAttackTemplate(event.target.value)} sx={FIELD_SX}>
                        <MenuItem value="">No imported template</MenuItem>
                        {templates.map(template => <MenuItem key={template.id} value={String(template.id)}>
                            {template.name || `Template #${template.id}`}{template.ownerUsername ? ` — ${template.ownerUsername}` : ''}
                        </MenuItem>)}
                    </TextField>
                </Field>
            </Box>

            <Box sx={GROUP_SX}>
                <GoldCheckbox checked={draft.useWarWagon} onChange={value => update(next => { next.useWarWagon = value })} label="Use War Wagon on every attack" />
                <Helper>Places an available Sceat War Wagon and uses its additional-wave bonus. If unavailable, the normal attack plan is used.</Helper>
            </Box>

            <Box sx={GROUP_SX}>
                <GoldCheckbox checked={draft.espionageEnabled} onChange={value => update(next => { next.espionageEnabled = value })} label="Espionage every event target" />
                <Helper>When enabled, every matching Foreign Invader/Bloodcrow target is scouted at 100% using feathers (PTT). Attacks start only after the spy report is available.</Helper>
            </Box>

            <Box sx={GROUP_SX}>
                <Typography sx={{ color: '#f5f7ff', fontWeight: 850, fontSize: '1rem' }}>Glory tools for waves 2+</Typography>
                <Helper>Select which glory banners the bot may use. Every selected wave is filled to the maximum allowed amount, starting with the highest glory bonus that is in stock.</Helper>
                <Box sx={{ mt: 1.3, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2,minmax(0,1fr))', xl: 'repeat(4,minmax(0,1fr))' }, gap: 1 }}>
                    {FOREIGN_GLORY_TOOLS.map(tool => {
                        const checked = selectedGloryTools.has(tool.wodID)
                        const stock = gloryStock.has(tool.wodID) ? gloryStock.get(tool.wodID).toLocaleString() : 'Waiting'
                        return <Box key={tool.wodID} onClick={() => toggleGloryTool(tool.wodID, !checked)} sx={{ p: 1, cursor: 'pointer', borderRadius: '8px', border: checked ? '1px solid rgba(143,180,255,.8)' : '1px solid rgba(129,146,178,.18)', backgroundColor: checked ? 'rgba(68,110,183,.2)' : 'rgba(10,20,36,.52)', opacity: checked ? 1 : .62 }}>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <Checkbox checked={checked} onChange={event => { event.stopPropagation(); toggleGloryTool(tool.wodID, event.target.checked) }} onClick={event => event.stopPropagation()} sx={{ p: .25, color: 'rgba(143,180,255,.5)', '&.Mui-checked': { color: '#8fb4ff' } }} />
                                <UnitIconFrame iconUrl={tool.iconUrl} itemType="tool" size={46} />
                                <Box sx={{ minWidth: 0 }}>
                                    <Typography noWrap sx={{ color: '#f5f7ff', fontWeight: 800, fontSize: '.83rem' }}>{tool.label}</Typography>
                                    <Stack direction="row" spacing={.7} sx={{ mt: .35 }}><Chip size="small" label={`+${tool.fameBonus}% glory`} color="primary" /><Chip size="small" label={`Stock: ${stock}`} /></Stack>
                                </Box>
                            </Stack>
                        </Box>
                    })}
                </Box>
            </Box>

            <Alert severity="success">FILL WAVES is active: soldiers fill every available wave and the courtyard/RW. Wave 1 covers the scouted wall, gate and ranged bonuses with the requested glory combo tools first, then uses the invasion ladder, ram and mask as fallback.</Alert>

            <Box sx={{ ...GROUP_SX, display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(4,minmax(0,1fr))' }, gap: 1 }}>
                {[['Event', runtimeStatus?.event ?? 'Waiting'], ['Glory', `${Number(runtimeStatus?.gloryScore ?? 0).toLocaleString()} / ${runtimeStatus?.gloryLimit ? Number(runtimeStatus.gloryLimit).toLocaleString() : 'No limit'}`], ['Targets', runtimeStatus?.detectedTargets ?? 0], ['Commanders / targets', `${runtimeStatus?.commanderAttackAttempts ?? 0} / ${runtimeStatus?.uniqueTargetsAttempted ?? 0}`]].map(([label, value]) => <Box key={label}><Typography variant="caption" color="text.secondary">{label}</Typography><Typography sx={{ color: '#f5f7ff', fontWeight: 850 }}>{value}</Typography></Box>)}
            </Box>
            <Alert severity="warning" icon={<ShieldRoundedIcon />}>The module uses real troops and tools. It sends an attack only after the spy report is valid, a selected commander is still dispatchable and the generated army meets the game minimum.</Alert>
        </Stack>
    </Paper>
}
